import {
  DollarOutlined,
  FileSyncOutlined,
  FilterFilled,
  PlusOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Radio,
  Space,
  Switch,
  Tooltip,
  message,
} from 'antd';
import { debounce, isBoolean } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { GoogleChat, GoogleMeet, UserResume } from '../../assets/svg';
import api from '../../common/api';
import {
  ADD_USER_CHAT_OR_SCRUM_REF_TYPE,
  ADD_USER_TO_CHAT_OR_MEET,
  BILLING_TYPE,
  ERROR_MESSAGE,
  LEVEL,
  MODULES_KEY,
  PERMISSION_OBJECT,
  ROLE_KEYS,
  ROUTES,
  ROUTES_MODULES_KEY,
  SESSION_EXPIRED,
  SYNC_MESSAGE,
  USER,
  USERS_TABLE_FIELDS,
} from '../../common/constants';
import CommonSelect from '../../components/CommonSelect';
import CommonTable from '../../components/CommonTable';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  antdSortOrder,
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import {
  ADD_USER_TO_CHAT_SPACES,
  ADD_USER_TO_MORNING_CALL,
} from './graphql/Mutations';
import {
  CHECK_AUTH_FOR_CALENDER,
  CHECK_AUTH_FOR_CHAT,
  DEPARTMENTS,
  DESIGNATIONS,
  GET_CALENDER_AUTH_URL,
  GET_CHAT_AUTH_URL,
  USERS,
} from './graphql/Queries';

const buildUsersFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchStrings: params?.tags,
  sortOrder: params?.sortOrder,
  sortField: params?.sortField,
  designation: params?.designation,
  departmentId: params?.departmentId,
  billable: params?.billable,
  showInactiveUsers: params?.showInactiveUsers,
});

function Users() {
  const history = useHistory();
  // eslint-disable-next-line no-undef
  const user = JSON.parse(localStorage?.getItem(USER));
  const [form] = Form?.useForm();
  const { params, setParams } = useTableQueryParams({
    fields: USERS_TABLE_FIELDS,
  });
  const [filterVisible, setFilterVisible] = useState({
    nameFilterVisible: false,
    designationFilterVisible: false,
    departmentFilterVisible: false,
    billableFilterVisible: false,
  });
  // Draft values for the Apply/Reset column filter popups — only committed
  // to the URL (and re-fetched) once "Apply" is clicked.
  const [draftFilter, setDraftFilter] = useState({
    sortField: params?.sortField,
    designation: params?.designation,
    departmentId: params?.departmentId,
    billable: params?.billable,
  });
  const [checked, setChecked] = useState(params?.sortField === 'lastName');
  const [disabledButtons, setDisabledButtons] = useState({});
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [designationSearch, setDesignationSearch] = useState('');
  const [designationDataSearch, setDesignationDataSearch] = useState('');
  const isUserCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.USERS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });

  const [executeQuery, { loading, data }] = useLazyQuery(USERS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    executeQuery({ variables: { filters: buildUsersFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const [addUserToChatSpace] = useMutation(ADD_USER_TO_CHAT_SPACES, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [addUserToMorningCall] = useMutation(ADD_USER_TO_MORNING_CALL, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [checkAuthForChat] = useLazyQuery(CHECK_AUTH_FOR_CHAT, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [checkAuthForCalendar] = useLazyQuery(CHECK_AUTH_FOR_CALENDER, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [getChatAuthUrl] = useLazyQuery(GET_CHAT_AUTH_URL, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [getCalenderAuthUrl] = useLazyQuery(GET_CALENDER_AUTH_URL, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const handleSearch = debounce((value) => {
    setDesignationDataSearch('');
    setParams({
      tags: value?.length > 0 ? value?.toString()?.trim()?.split(',') : [],
      page: 1,
    });
  }, 700);

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  const handleReset = (prop) => {
    const patch = {
      name: { sortField: 'displayName' },
      designation: { designation: null },
      department: { departmentId: null },
      [BILLING_TYPE?.BILLABLE]: { billable: undefined },
    }[prop];
    setDraftFilter((prev) => ({ ...prev, ...patch }));
    setParams({ ...patch, page: 1 });
    if (prop === BILLING_TYPE?.BILLABLE) {
      form?.resetFields();
    }
  };

  const handleBillableFilter = (e) => {
    setDraftFilter((prev) => ({ ...prev, billable: e?.target?.value }));
  };
  const handleNameFilter = (e) => {
    setChecked(e?.target?.checked);
    if (e?.target?.checked) {
      setDraftFilter((prev) => ({ ...prev, sortField: e?.target?.name }));
    }
  };
  const handleDesignation = (value) => {
    setDraftFilter((prev) => ({ ...prev, designation: value }));
    setDesignationSearch('');
  };

  const handleDesignationSearch = debounce((value) => {
    setDesignationSearch(value);
  }, 500);

  const handleDesignationDataSearch = debounce((value) => {
    setDesignationDataSearch(value);
  }, 500);

  const handleDepartment = (value) => {
    setDraftFilter((prev) => ({ ...prev, departmentId: value }));
    setDepartmentSearch('');
  };

  const handleDepartmentSearch = debounce((value) => {
    setDepartmentSearch(value);
  }, 500);

  const handleUserResume = (recordId) => {
    // eslint-disable-next-line no-undef
    window.open(`${ROUTES_MODULES_KEY?.USERS}/${recordId}${ROUTES?.RESUME}`);
  };

  const handleFilter = () => {
    setParams({ ...draftFilter, page: 1 });
    setFilterVisible({
      nameFilterVisible: false,
      designationFilterVisible: false,
      departmentFilterVisible: false,
      billableFilterVisible: false,
    });
  };

  const getNameFilterProps = (dataIndex) => ({
    filterDropdownOpen: filterVisible?.nameFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        nameFilterVisible: visible,
      }),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Checkbox
            name="lastName"
            checked={checked}
            onChange={handleNameFilter}
          >
            {dataIndex}
          </Checkbox>
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                handleReset('name');
                setChecked(false);
                setFilterVisible({
                  ...filterVisible,
                  nameFilterVisible: false,
                });
              }}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.sortField === 'lastName' ? 'filter-filled-data' : ''}
      />
    ),
  });

  const getJobTitleFilterProps = () => ({
    filterDropdownOpen: filterVisible?.designationFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        designationFilterVisible: visible,
      }),
    filterSearch: true,
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <CommonSelect
            placeholder="Select Designation"
            showSearch
            query={DESIGNATIONS}
            uniqByVariable="jobTitle"
            onChange={handleDesignation}
            onSearch={handleDesignationSearch}
            variables={{ searchString: designationSearch }}
            useEffectDeps={[designationSearch]}
            responsePath="designations.designations"
            valuePath="jobTitle"
            labelPath="jobTitle"
            fetchPolicy="network-only"
            value={draftFilter?.designation}
            virtual={false}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                handleReset('designation');
                setFilterVisible({
                  ...filterVisible,
                  designationFilterVisible: false,
                });
              }}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.designation ? 'filter-filled-data' : ''}
      />
    ),
  });

  const getDepartmentFilterProps = () => ({
    filterDropdownOpen: filterVisible?.departmentFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        departmentFilterVisible: visible,
      }),
    filterSearch: true,
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <CommonSelect
            placeholder="Select Department"
            showSearch
            query={DEPARTMENTS}
            onChange={handleDepartment}
            onSearch={handleDepartmentSearch}
            variables={{ search: departmentSearch }}
            useEffectDeps={[departmentSearch]}
            responsePath="departments.departments"
            valuePath="id"
            labelPath="name"
            fetchPolicy="network-only"
            value={draftFilter?.departmentId}
            resolveMissingSelected
            virtual={false}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                handleReset('department');
                setFilterVisible({
                  ...filterVisible,
                  departmentFilterVisible: false,
                });
              }}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.departmentId ? 'filter-filled-data' : ''}
      />
    ),
  });

  const getProjectFilterProps = () => ({
    filterDropdownOpen: filterVisible?.billableFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        billableFilterVisible: visible,
      }),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Form name="control-ref" form={form} layout="vertical">
            <Form.Item name="Billable" valuePropName="checked">
              <Radio.Group onChange={handleBillableFilter}>
                <Space direction="vertical">
                  <Radio value>
                    {BILLING_TYPE?.BILLABLE?.charAt(0)?.toUpperCase()}
                    {BILLING_TYPE?.BILLABLE?.slice(1)}
                  </Radio>
                  <Radio value={false}>
                    {BILLING_TYPE?.NON_BILLABLE?.charAt(0)?.toUpperCase()}
                    {BILLING_TYPE?.NON_BILLABLE?.slice(1)}
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Form>
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                handleReset(BILLING_TYPE?.BILLABLE);
                setFilterVisible({
                  ...filterVisible,
                  billableFilterVisible: false,
                });
              }}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
    render: (value) =>
      value && (
        <div className="billable">
          <DollarOutlined />
        </div>
      ),
    filterIcon: () => (
      <FilterFilled
        className={isBoolean(params?.billable) ? 'filter-filled-data' : ''}
      />
    ),
  });

  const handleSync = async () => {
    const syncUrls = [
      process.env.REACT_APP_SYNC_USERS_URL,
      process.env.REACT_APP_SYNC_DEPARTMENT_URL,
    ];
    try {
      await Promise?.all(
        syncUrls?.map(async (url) => {
          await api?.post(url);
        }),
      );
      message?.destroy();
      message?.success(SYNC_MESSAGE);
    } catch (error) {
      if (
        error?.response?.data?.errors?.[0]?.extensions?.code === SESSION_EXPIRED
      ) {
        message?.destroy();
        message?.error(ERROR_MESSAGE);
        history?.replace({
          pathname: ROUTES?.AUTHENTICATION,
          // eslint-disable-next-line no-undef
          state: { from: window?.location?.pathname },
        });
      }
    }
  };

  const handleToggleSwitch = (event) => {
    setParams({ showInactiveUsers: event });
  };

  const handleAddUserIntoChat = async (value, actionType) => {
    setDisabledButtons((prev) => ({
      ...prev,
      [value?.id]: {
        ...prev[value?.id],
        [actionType]: true,
      },
    }));
    try {
      const {
        data: {
          checkAuthForChat: { isAuthenticatedForChat },
        },
      } = await checkAuthForChat({
        variables: {
          input: {
            uniqueId: process.env.REACT_APP_ADD_USER_INTO_SPACE_AND_CHAT,
            refType:
              ADD_USER_CHAT_OR_SCRUM_REF_TYPE.COMMON_SPACE_MEMBERS_MANAGEMENT,
          },
        },
      });

      if (isAuthenticatedForChat) {
        await addUserToChatSpace({
          variables: {
            where: {
              id: value?.id,
            },
          },
        });
      } else {
        const {
          data: {
            getChatAuthUrl: { url },
          },
        } = await getChatAuthUrl({
          variables: {
            input: {
              uniqueId: process.env.REACT_APP_ADD_USER_INTO_SPACE_AND_CHAT,
              refType:
                ADD_USER_CHAT_OR_SCRUM_REF_TYPE.COMMON_SPACE_MEMBERS_MANAGEMENT,
            },
          },
        });
        if (url) {
          // eslint-disable-next-line no-undef
          window?.open(url);
        }
      }
    } catch (error) {
      return error;
    } finally {
      setDisabledButtons((prev) => ({
        ...prev,
        [value?.id]: {
          ...prev[value?.id],
          [actionType]: false,
        },
      }));
    }
  };

  const handleAddUserIntoScrumCall = async (value, actionType) => {
    setDisabledButtons((prev) => ({
      ...prev,
      [value?.id]: {
        ...prev[value?.id],
        [actionType]: true,
      },
    }));
    try {
      const {
        data: {
          checkAuthForCalendar: { isAuthenticatedForCalendar },
        },
      } = await checkAuthForCalendar({
        variables: {
          input: {
            uniqueId: process.env.REACT_APP_ADD_USER_INTO_SPACE_AND_CHAT,
            refType:
              ADD_USER_CHAT_OR_SCRUM_REF_TYPE.MORNING_CALL_MEMBERS_MANAGEMENT,
          },
        },
      });

      if (isAuthenticatedForCalendar) {
        await addUserToMorningCall({
          variables: {
            where: {
              id: value?.id,
            },
          },
        });
      } else {
        const {
          data: {
            getCalendarAuthUrl: { url },
          },
        } = await getCalenderAuthUrl({
          variables: {
            input: {
              refType:
                ADD_USER_CHAT_OR_SCRUM_REF_TYPE.MORNING_CALL_MEMBERS_MANAGEMENT,
            },
          },
        });
        if (url) {
          // eslint-disable-next-line no-undef
          window?.open(url);
        }
      }
    } catch (e) {
      return e;
    } finally {
      setDisabledButtons((prev) => ({
        ...prev,
        [value?.id]: {
          ...prev[value?.id],
          [actionType]: false,
        },
      }));
    }
  };

  const handleSummarySync = async () => {
    const syncUrls = process.env.REACT_APP_SYNC_SUMMARY;
    try {
      await new Promise((resolve, reject) => {
        api
          ?.post(syncUrls)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
      message?.destroy();
      message?.success(SYNC_MESSAGE);
    } catch (error) {
      if (
        error?.response?.data?.errors?.[0]?.extensions?.code === SESSION_EXPIRED
      ) {
        message?.destroy();
        message?.error(ERROR_MESSAGE);
        history?.replace({
          pathname: ROUTES?.AUTHENTICATION,
          // eslint-disable-next-line no-undef
          state: { from: window?.location?.pathname },
        });
      }
    }
  };

  const handleSyncSummaryById = async (userID) => {
    const syncUrls = `${process.env.REACT_APP_SYNC_SUMMARY}/${userID}`;
    try {
      await new Promise((resolve, reject) => {
        api
          ?.post(syncUrls)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
      message?.destroy();
      message?.success(SYNC_MESSAGE);
    } catch (error) {
      if (
        error?.response?.data?.errors?.[0]?.extensions?.code === SESSION_EXPIRED
      ) {
        message?.destroy();
        message?.error(ERROR_MESSAGE);
        history?.replace({
          pathname: ROUTES?.AUTHENTICATION,
          // eslint-disable-next-line no-undef
          state: { from: window?.location?.pathname },
        });
      }
    }
  };

  const columns = [
    {
      dataIndex: 'billable',
      key: 'billable',
      className: 'billable-filter',
      ...getProjectFilterProps(),
    },
    {
      title: 'Add',
      key: 'Add',
      render: (record) => (
        <Space size="small">
          <Tooltip title="Add User To Chat">
            <Button
              className="border-none"
              disabled={disabledButtons[record?.id]?.chat}
            >
              <GoogleChat
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddUserIntoChat(record, ADD_USER_TO_CHAT_OR_MEET.CHAT);
                }}
              />
            </Button>
          </Tooltip>
          <Tooltip title="Add User To Morning Call">
            <Button
              className="border-none"
              disabled={disabledButtons[record?.id]?.meet}
            >
              <GoogleMeet
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddUserIntoScrumCall(
                    record,
                    ADD_USER_TO_CHAT_OR_MEET.MEET,
                  );
                }}
              />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Resume',
      align: 'center',
      render: (record) =>
        !record?.isIntern && (
          <Button
            className="user-resume-btn"
            type="text"
            onClick={(e) => {
              e?.stopPropagation();
              handleUserResume(record?.id);
            }}
            target="_blank"
          >
            <UserResume />
          </Button>
        ),
    },
    ...([ROLE_KEYS?.SUPER_ADMIN, ROLE_KEYS?.ADMIN]?.includes(user?.role) ||
    [LEVEL?.L1, LEVEL?.L2]?.includes(user?.level?.name)
      ? [
          {
            title: 'Summary',
            align: 'center',
            render: (record) =>
              !record?.isIntern && (
                <Tooltip
                  title={`Click here to update ${record?.displayName}'s professional summaries.`}
                >
                  <Button
                    className="user-sync-btn"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleSyncSummaryById(record?.id);
                    }}
                  >
                    <FileSyncOutlined />
                  </Button>
                </Tooltip>
              ),
          },
        ]
      : []),
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: true,
      filterMultiple: false,
      ...getNameFilterProps('Last Name'),
      width: '25%',
      sortOrder: antdSortOrder(params, 'displayName'),
      className: 'link-custom-width',
      render: (_, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.USERS}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.displayName}
        </Link>
      ),
    },
    {
      title: 'Designation',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      ...getJobTitleFilterProps(),
      width: '25%',
      className: 'link-custom-width',
      render: (_, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.USERS}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.jobTitle}
        </Link>
      ),
    },
    {
      title: 'Department',
      dataIndex: ['department', 'name'],
      key: 'department',
      ...getDepartmentFilterProps(),
      width: '25%',
      className: 'link-custom-width',
      render: (_, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.USERS}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.department?.name}
        </Link>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      width: '25%',
      sortOrder: antdSortOrder(params, 'email'),
      className: 'link-custom-width',
      render: (_, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.USERS}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.email}
        </Link>
      ),
    },
  ];

  const renderMaxTag = (omittedValues) => {
    if (omittedValues?.length === 0) {
      return null;
    }

    return <span>+ {omittedValues?.length} more...</span>;
  };

  const handleRowClick = (record) => {
    history?.push(`${ROUTES_MODULES_KEY?.USERS}/${record?.id}`);
  };

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="Users"
        extra={
          <>
            <div className="project-filter-right">
              <div className="project-filter">
                {([ROLE_KEYS?.SUPER_ADMIN, ROLE_KEYS?.ADMIN]?.includes(
                  user?.role,
                ) ||
                  [LEVEL?.L1, LEVEL?.L2]?.includes(user?.level?.name)) && (
                  <Tooltip placement="bottom" title={<span>User Sync</span>}>
                    <Button onClick={handleSync}>
                      <SyncOutlined />
                    </Button>
                  </Tooltip>
                )}
                {([ROLE_KEYS?.SUPER_ADMIN, ROLE_KEYS?.ADMIN]?.includes(
                  user?.role,
                ) ||
                  [LEVEL?.L1, LEVEL?.L2]?.includes(user?.level?.name)) && (
                  <Tooltip
                    placement="bottom"
                    title={
                      <span>
                        Click here to update all employee's professional
                        summaries.
                      </span>
                    }
                  >
                    <Button>
                      <FileSyncOutlined onClick={handleSummarySync} />
                    </Button>
                  </Tooltip>
                )}
                <span>
                  <Tooltip
                    title={
                      params?.showInactiveUsers
                        ? 'Include Inactive Users'
                        : 'All Active Users'
                    }
                  >
                    <Switch
                      checkedChildren="All"
                      unCheckedChildren="Active"
                      checked={params?.showInactiveUsers}
                      onChange={handleToggleSwitch}
                    />
                  </Tooltip>
                </span>
                {isUserCreatePermission && (
                  <Button
                    key="1"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      history?.push({
                        pathname: `${ROUTES_MODULES_KEY?.USERS}/create`,
                      });
                    }}
                  >
                    Create User
                  </Button>
                )}
                <span className="user-select-tags">
                  <CommonSelect
                    mode="tags"
                    placeholder="Search Name, Department, Designation"
                    showSearch
                    query={DESIGNATIONS}
                    onChange={handleSearch}
                    onSearch={handleDesignationDataSearch}
                    variables={{ searchString: designationDataSearch }}
                    useEffectDeps={[designationDataSearch]}
                    responsePath="designations.designations"
                    valuePath="jobTitle"
                    labelPath="jobTitle"
                    fetchPolicy="network-only"
                    uniqByVariable="jobTitle"
                    onClear={() => setParams({ tags: [], page: 1 })}
                    value={params?.tags}
                    maxTagCount={3}
                    maxTagPlaceholder={renderMaxTag}
                    virtual={false}
                  />
                </span>
              </div>
            </div>
          </>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            columns={columns}
            dataSource={data?.usersAdmin?.users}
            rowKey={(obj) => obj?.id}
            onChange={onChange}
            loadingData={loading}
            bordered
            paginationConfig={{
              current: params?.page,
              total: data?.usersAdmin?.count,
              pageSize: params?.pageSize,
            }}
            onRow={(record) => ({ onClick: () => handleRowClick(record) })}
            rowClassName="user-table-row"
            className="user-table"
          />
        </div>
      </Card>
    </div>
  );
}

export default Users;
